/**
 * Select — native select with label, hint, and error states.
 */

import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import type { SelectOption } from '@/types/common';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
  wrapperClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, hint, error, options, placeholder, className, wrapperClassName, id, ...props }, ref) => {
    const selectId = id || props.name;

    return (
      <div className={cn('flex flex-col gap-2', wrapperClassName)}>
        {label && (
          <label htmlFor={selectId} className="text-sm font-medium text-foreground tracking-tight">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-surface px-3 py-2 text-sm',
            'transition-all duration-fast ease-standard',
            'hover:border-neutral-400',
            'focus-visible:outline-none focus-visible:border-primary-600 focus-visible:ring-2 focus-visible:ring-primary-600/15',
            'disabled:cursor-not-allowed disabled:opacity-40 disabled:bg-surface-secondary',
            error && 'border-error-400 focus-visible:border-error-500 focus-visible:ring-error-500/15',
            className,
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        {error ? (
          <p id={`${selectId}-error`} className="text-xs text-error-600">
            {error}
          </p>
        ) : hint ? (
          <p id={`${selectId}-hint`} className="text-xs text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);

Select.displayName = 'Select';
